import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Plus, Pencil, Trash2, ChevronRight, ChevronDown, Search, Package, FolderOpen, GripVertical, Table2, Variable, Globe, X, Copy, Upload, Image, Share2, Building2, Tag, Eye, EyeOff } from 'lucide-react';
import ImportDialog from '../components/ImportDialog';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { useToast } from '../../../components/Toast';
import Modal from '../../../components/Modal';
import ConfirmDialog from '../../../components/ConfirmDialog';
import api from '../../../services/api';
import MiniSpreadsheet from '../components/MiniSpreadsheet';

// ==================== HELPER: budowanie drzewa ====================

function buildTree(flatItems) {
  const map = {};
  const roots = [];
  flatItems.forEach(item => { map[item.id] = { ...item, children: [] }; });
  flatItems.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(map[item.id]);
    } else {
      roots.push(map[item.id]);
    }
  });
  const sortChildren = (nodes) => {
    nodes.sort((a, b) => a.display_order - b.display_order);
    nodes.forEach(n => sortChildren(n.children));
  };
  sortChildren(roots);
  return roots;
}

// ==================== TREE NODE ====================

function TreeNode({ node, depth, canEdit, canDelete, canCreate, onEdit, onDelete, onAdd, onMove, onSpreadsheetSave,
  activeSpreadsheet, setActiveSpreadsheet, dragState, setDragState, variables, parentExports = [], panelId, onCrossDrop, onImages, sharedId, onDuplicate, shareDiscounts, shareMargins }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const isGroup = node.item_type === 'group';
  const hasChildren = node.children.length > 0;
  const nodeRef = useRef(null);
  const isSpreadsheetOpen = activeSpreadsheet === node.id;
  const hasSpreadsheet = node.spreadsheet && node.spreadsheet.columns && node.spreadsheet.columns.length > 0;

  const formatPrice = (p) => Number(p || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleDragStart = (e) => {
    e.stopPropagation();
    setDragState({ draggingId: node.id, overId: null, overPosition: null, sourcePanel: panelId });
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('application/x-pricelist-item', JSON.stringify({ id: node.id, panelId, sharedId: sharedId || null }));
    setTimeout(() => { if (nodeRef.current) nodeRef.current.style.opacity = '0.4'; }, 0);
  };
  const handleDragEnd = (e) => {
    e.stopPropagation();
    if (nodeRef.current) nodeRef.current.style.opacity = '1';
    if (dragState.overId && dragState.draggingId !== dragState.overId) {
      onMove(dragState.draggingId, dragState.overId, dragState.overPosition);
    }
    setDragState({ draggingId: null, overId: null, overPosition: null, sourcePanel: null });
  };
  const handleDragOver = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (dragState.draggingId === node.id) return;
    const rect = nodeRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    let pos = y < h / 2 ? 'before' : 'after';
    if (isGroup && y > h * 0.25 && y < h * 0.75) pos = 'inside';

    // Cross-panel detection
    if (dragState.sourcePanel && dragState.sourcePanel !== panelId) {
      e.dataTransfer.dropEffect = 'copy';
    }
    setDragState(prev => ({ ...prev, overId: node.id, overPosition: pos, targetPanel: panelId }));
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/x-pricelist-item'));
      if (data.panelId !== panelId && onCrossDrop) {
        const pos = dragState.overPosition;
        const parentId = pos === 'inside' ? node.id : (node.parent_id || null);
        onCrossDrop(data.id, data.panelId, parentId, data.sharedId);
      }
    } catch { /* same-panel move handled by dragEnd */ }
  };

  const isDropTarget = dragState.overId === node.id && dragState.draggingId !== node.id;
  const isCrossPanel = dragState.sourcePanel && dragState.sourcePanel !== panelId;
  let dropClass = '';
  if (isDropTarget) {
    const color = isCrossPanel ? 'blue' : 'emerald';
    if (dragState.overPosition === 'before') dropClass = `border-t-2 border-t-${color}-500`;
    else if (dragState.overPosition === 'after') dropClass = `border-b-2 border-b-${color}-500`;
    else if (dragState.overPosition === 'inside') dropClass = `ring-2 ring-${color}-500 ring-inset`;
  }

  return (
    <div>
      <div ref={nodeRef} draggable={canEdit || !!sharedId}
        onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDrop={handleDrop}
        className={`flex items-center gap-1 py-1.5 px-2 rounded-md hover:bg-gray-50 group transition-all select-none ${dropClass} ${isSpreadsheetOpen ? 'bg-emerald-50' : ''}`}
        style={{ paddingLeft: `${depth * 20 + 4}px` }}>
        {(canEdit || sharedId) && <span className="cursor-grab opacity-0 group-hover:opacity-40 hover:!opacity-100 flex-shrink-0" onMouseDown={e => e.preventDefault()}><GripVertical size={16} /></span>}
        {isGroup ? (
          <button onClick={() => setExpanded(!expanded)} className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded">
            {expanded ? <ChevronDown size={15} className="text-gray-500" /> : <ChevronRight size={15} className="text-gray-500" />}
          </button>
        ) : <span className="w-[20px] flex-shrink-0" />}
        {isGroup ? <FolderOpen size={16} className="text-amber-500 flex-shrink-0" /> : <Package size={16} className="text-gray-400 flex-shrink-0" />}
        <span className={`flex-1 text-sm truncate cursor-pointer ${isGroup ? 'font-medium text-gray-800' : 'text-gray-700'} ${isSpreadsheetOpen ? 'text-emerald-700' : ''}`}
          onClick={() => setActiveSpreadsheet(isSpreadsheetOpen ? null : node.id)} title={node.description || 'Kliknij = arkusz'}>
          {node.name}
          {node.description && <span className="ml-1.5 text-sm text-gray-400 font-normal">{node.description.length > 30 ? node.description.substring(0, 30) + '...' : node.description}</span>}
          {node.spreadsheet?.rows?.length === 1 && (() => {
            const row = node.spreadsheet.rows[0];
            const strip = v => (v || '').replace(/^'/, '');
            const d = strip(row?.D || row?.d);
            const c = strip(row?.C || row?.c);
            return (d || c) ? <span className="ml-1.5 text-xs text-blue-500 font-normal">{d}{c ? '/' + c : ''}</span> : null;
          })()}
        </span>
        {hasSpreadsheet && <span className={`flex-shrink-0 ${isSpreadsheetOpen ? 'text-emerald-500' : 'text-gray-300'}`}><Table2 size={16} /></span>}
        {/* Wartość — z rozbiciem cen dla udostępnionych cenników */}
        {shareDiscounts && node.spreadsheet?.rows?.length > 0 ? (() => {
          // Cena pojedynczej gałęzi — pierwszy wiersz, kolumna D
          const row = node.spreadsheet.rows[0];
          const basePrice = parseFloat(String(row?.D || '').replace(/^'/, '').replace(/\s/g,'').replace(',','.')) || 0;
          if (basePrice <= 0) return null;

          let totalMarkup = 0, totalDiscount = 0, conditionalDetails = [];
          (shareDiscounts || []).filter(d => d.visible_to_recipient).forEach(d => {
            const val = parseFloat(d.value) || 0;
            const amount = d.discount_type === 'percent' ? basePrice * val / 100 : val;
            const hasCondition = d.condition_min != null || d.condition_max != null;
            if (d.is_markup) {
              if (!hasCondition) totalMarkup += amount;
            } else {
              if (hasCondition) {
                const cost = basePrice - amount;
                const rangeMin = d.condition_min != null ? parseFloat(d.condition_min) : null;
                const rangeMax = d.condition_max != null ? parseFloat(d.condition_max) : null;
                const rangeLabel = rangeMin != null && rangeMax != null ? `${rangeMin}–${rangeMax}` : rangeMin != null ? `od ${rangeMin}` : `do ${rangeMax}`;
                conditionalDetails.push({ amount, cost, val, type: d.discount_type, rangeLabel });
              } else {
                totalDiscount += amount;
              }
            }
          });
          const bestConditionalDiscount = conditionalDetails.length > 0 ? Math.min(...conditionalDetails.map(c => c.amount)) : 0;
          const displayDiscount = totalDiscount > 0 ? totalDiscount : bestConditionalDiscount;

          // Margin
          const globalMargin = (shareMargins || []).find(m => m.scope === 'global' && !m.target_item_id);
          let marginAmount = 0;
          if (globalMargin) {
            const mv = parseFloat(globalMargin.margin_value) || 0;
            marginAmount = globalMargin.margin_type === 'percent' ? basePrice * mv / 100 : mv;
          }
          const sellPrice = basePrice + totalMarkup + marginAmount;
          const myCost = basePrice - displayDiscount;

          // Unit from column C
          const unitC = String(row?.C || '').replace(/^'/, '').trim();

          // Tooltip
          const hasConditional = conditionalDetails.length > 0;
          const marginPct = globalMargin && globalMargin.margin_type === 'percent' ? `${parseFloat(globalMargin.margin_value)}%` : null;
          const marginAbs = globalMargin && globalMargin.margin_type !== 'percent' ? `${parseFloat(globalMargin.margin_value)} zł` : null;
          const tooltipLines = [];
          tooltipLines.push(`${t('pricelists.tooltipBasePrice')}: ${formatPrice(basePrice)} zł/${unitC || 'szt'}`);
          if (displayDiscount > 0.01) tooltipLines.push(`${t('pricelists.tooltipAfterDiscount')}: ${formatPrice(myCost)} zł/${unitC || 'szt'}`);
          if (marginAmount > 0.01 || totalMarkup > 0.01) tooltipLines.push(`${t('pricelists.tooltipWithMargin')}${marginPct ? ` (${marginPct})` : marginAbs ? ` (${marginAbs})` : ''}: ${formatPrice(sellPrice)} zł/${unitC || 'szt'}`);
          if (hasConditional) {
            tooltipLines.push('');
            tooltipLines.push(`${t('pricelists.tooltipConditionalDiscounts')}:`);
            conditionalDetails.forEach(c => {
              tooltipLines.push(`  ${c.rangeLabel}${unitC ? ' ' + unitC : ' szt'}: ${t('pricelists.tooltipDiscount')} ${c.val}${c.type === 'percent' ? '%' : ' zł'} → ${formatPrice(c.cost)} zł/${unitC || 'szt'}`);
            });
          }
          const tooltip = tooltipLines.join('\n');

          return (
            <span className="text-right flex-shrink-0 ml-2 flex items-center gap-1 tabular-nums"
              title={tooltip || undefined}>
              <span className="text-xs text-gray-400">{formatPrice(basePrice)}</span>
              {displayDiscount > 0.01 && <><span className="text-gray-300">|</span><span className="text-xs text-green-600 font-medium">{formatPrice(myCost)}</span></>}
              {(totalMarkup > 0.01 || marginAmount > 0.01) && <><span className="text-gray-300">|</span><span className="text-xs text-blue-700 font-bold">{formatPrice(sellPrice)}</span></>}
              {hasConditional && <span className="text-green-500 text-[9px] cursor-help">*</span>}
              {unitC && <span className="text-xs text-gray-400">/{unitC}</span>}
            </span>
          );
        })() : node.result_value > 0 ? (
          <span className="text-xs text-gray-500 flex-shrink-0 w-20 text-right mr-1 tabular-nums">
            {formatPrice(node.result_value)}
          </span>
        ) : null}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0">
          {canCreate && isGroup && <button onClick={() => onAdd(node.id, 'item')} className="p-0.5 hover:bg-emerald-50 rounded" title="Dodaj pozycję"><Plus size={16} className="text-emerald-600" /></button>}
          {canCreate && isGroup && <button onClick={() => onAdd(node.id, 'group')} className="p-0.5 hover:bg-amber-50 rounded" title="Dodaj podgrupę"><FolderOpen size={16} className="text-amber-500" /></button>}
          {onImages && <button onClick={() => onImages(node)} className="p-0.5 hover:bg-blue-50 rounded" title="Zdjęcia"><Image size={16} className="text-blue-500" /></button>}
          {canCreate && <button onClick={() => onDuplicate && onDuplicate(node)} className="p-0.5 hover:bg-blue-50 rounded" title="Duplikuj gałąź"><Copy size={16} className="text-blue-500" /></button>}
          {canEdit && <button onClick={() => onEdit(node)} className="p-0.5 hover:bg-gray-100 rounded" title="Edytuj"><Pencil size={16} className="text-gray-500" /></button>}
          {canDelete && <button onClick={() => onDelete(node)} className="p-0.5 hover:bg-red-50 rounded" title="Usuń"><Trash2 size={16} className="text-red-400" /></button>}
        </div>
      </div>
      {isSpreadsheetOpen && (
        <div className="mr-1 mb-1 mt-0.5" style={{ paddingLeft: `${depth * 20 + 24}px` }}>
          <MiniSpreadsheet data={node.spreadsheet} readOnly={!canEdit} variables={variables} parentExports={parentExports}
            showTotal={false} onChange={(ss, resultValue) => onSpreadsheetSave(node.id, ss, resultValue)} />
        </div>
      )}
      {isGroup && expanded && hasChildren && (() => {
        const nodeExports = [];
        const ss = node.spreadsheet;
        if (ss && ss.exports && ss.rows) {
          // Build variables map
          const varMap = {};
          if (variables) {
            (variables.global || []).forEach(v => { varMap[v.key] = parseFloat(v.value) || 0; });
            (variables.local || []).forEach(v => { varMap[v.key] = parseFloat(v.value) || 0; });
          }
          parentExports.forEach(pe => { varMap[`e_${pe.label}`] = parseFloat(pe.value) || 0; });

          // Helper: resolve a cell value recursively (handles formulas referencing other cells)
          const resolveCell = (row, colKey, rows, rowIdx, visited = new Set()) => {
            const raw = row[colKey];
            if (raw === undefined || raw === null || raw === '') return 0;
            const s = String(raw);
            if (s.startsWith("'")) return 0;
            if (!s.startsWith('=')) return parseFloat(s) || 0;

            const cacheKey = `${rowIdx}:${colKey}`;
            if (visited.has(cacheKey)) return 0;
            visited.add(cacheKey);

            let expr = s.substring(1);
            // Replace variables
            Object.entries(varMap).forEach(([k, v]) => {
              expr = expr.replace(new RegExp(`\\b${k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi'), String(v));
            });
            // Replace column refs recursively
            expr = expr.replace(/(?<![a-zA-Z_0-9])([a-zA-Z])(?![a-zA-Z_0-9])/g, (m, c) => {
              const k = c.toUpperCase();
              if (!row.hasOwnProperty(k) && !row.hasOwnProperty(c)) return m;
              return String(resolveCell(row, k, rows, rowIdx, new Set(visited)));
            });
            try {
              if (/^[\d\s+\-*/().,%<>=?:!&|]+$/.test(expr)) {
                const result = eval(expr);
                if (typeof result === 'number' && isFinite(result)) return Math.round(result * 100) / 100;
              }
            } catch {}
            return 0;
          };

          Object.entries(ss.exports).forEach(([ref, meta]) => {
            const [rowStr, colKey] = ref.split(':');
            const rowIdx = parseInt(rowStr);
            const row = ss.rows?.[rowIdx];
            if (row) {
              const val = resolveCell(row, colKey, ss.rows, rowIdx);
              nodeExports.push({ label: meta.label, description: meta.description, value: val, source: node.name });
            }
          });
        }
        const childExports = [...parentExports, ...nodeExports];
        return (
          <div>
            {node.children.map(child => (
              <TreeNode key={child.id} node={child} depth={depth + 1}
                canEdit={canEdit} canDelete={canDelete} canCreate={canCreate}
                onEdit={onEdit} onDelete={onDelete} onAdd={onAdd} onMove={onMove}
                onSpreadsheetSave={onSpreadsheetSave} activeSpreadsheet={activeSpreadsheet}
                setActiveSpreadsheet={setActiveSpreadsheet} dragState={dragState} setDragState={setDragState}
                variables={variables} parentExports={childExports} panelId={panelId} onCrossDrop={onCrossDrop} onImages={onImages} sharedId={sharedId} onDuplicate={onDuplicate}
                shareDiscounts={shareDiscounts} shareMargins={shareMargins} />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// ==================== PRICELIST PANEL (jeden z dwóch) ====================

// ==================== ITEM IMAGES ====================

function ItemImagesPanel({ itemId, canEdit, toast }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/pricelists/items/${itemId}/images`); setImages(r.data.images); } catch {}
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    for (const f of files) formData.append('images', f);
    try {
      await api.post(`/pricelists/items/${itemId}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${files.length} zdjęć dodano`);
      load();
    } catch { toast.error('Błąd uploadu'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/pricelists/images/${id}`); load(); } catch { toast.error('Błąd'); }
  };

  return (
    <div>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          {images.map(img => (
            <div key={img.id} className="relative group rounded-lg overflow-hidden border border-gray-200">
              <img src={`/uploads/pricelists/${img.filename}`} alt={img.original_name}
                className="w-full h-32 object-cover" />
              {canEdit && (
                <button onClick={() => handleDelete(img.id)}
                  className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded opacity-0 group-hover:opacity-100 transition">
                  <Trash2 size={14} />
                </button>
              )}
              <div className="px-2 py-1 text-sm text-gray-500 truncate">{img.original_name}</div>
            </div>
          ))}
        </div>
      )}
      {images.length === 0 && <div className="text-center py-8 text-gray-400 text-base mb-4">Brak zdjęć</div>}
      {canEdit && (
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
            onChange={e => handleUpload(Array.from(e.target.files))} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-base w-full justify-center">
            <Image size={18} /> {uploading ? 'Ładowanie...' : 'Dodaj zdjęcia'}
          </button>
        </div>
      )}
    </div>
  );
}

// ==================== EDIT PRICELIST FORM ====================

function EditPricelistForm({ pricelist, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: pricelist?.name || '', description: pricelist?.description || '',
    currency: pricelist?.currency || 'PLN', is_active: pricelist?.is_active ?? true,
    valid_from: pricelist?.valid_from?.split('T')[0] || '', valid_to: pricelist?.valid_to?.split('T')[0] || '',
  });
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-base";
  if (!pricelist) return null;
  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-3">
      <div><label className="block text-base font-medium text-gray-600 mb-1">Nazwa *</label>
        <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={ic} required autoFocus /></div>
      <div><label className="block text-base font-medium text-gray-600 mb-1">Opis</label>
        <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={ic} rows={2} /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><label className="block text-base font-medium text-gray-600 mb-1">Waluta</label>
          <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className={ic}>
            <option value="PLN">PLN</option><option value="EUR">EUR</option><option value="USD">USD</option>
          </select></div>
        <div><label className="block text-base font-medium text-gray-600 mb-1">Ważny od</label>
          <input type="date" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} className={ic} /></div>
        <div><label className="block text-base font-medium text-gray-600 mb-1">Ważny do</label>
          <input type="date" value={form.valid_to} onChange={e => setForm({ ...form, valid_to: e.target.value })} className={ic} /></div>
      </div>
      <label className="flex items-center gap-2 text-base">
        <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300" />
        Cennik aktywny
      </label>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-base text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" className="px-4 py-2 text-base bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">Zapisz</button>
      </div>
    </form>
  );
}

function SharedCompanyNode({ company, shares, onSelect }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-1">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50 rounded transition">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Building2 size={14} className="text-blue-500" />
        <span className="truncate">{company}</span>
        <span className="text-xs text-gray-400 ml-auto">{shares.length}</span>
      </button>
      {open && shares.map(s => {
        const discounts = s.discounts || [];
        return (
          <button key={s.id} onClick={() => onSelect(s)}
            className="w-full text-left pl-8 pr-3 py-1.5 rounded hover:bg-blue-50 text-sm transition">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-gray-700 truncate">{s.pricelist_name}</span>
              {discounts.map(d => (
                <span key={d.id} className={`text-[10px] px-1 py-0.5 rounded flex-shrink-0 ${d.is_markup ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                  {d.is_markup ? '+' : '-'}{d.value}{d.type === 'percent' ? '%' : 'zł'}
                  {d.condition_min != null || d.condition_max != null ? ` (${d.condition_min || ''}–${d.condition_max || ''} ${d.condition_unit || ''})` : ''}
                </span>
              ))}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {new Date(s.created_at).toLocaleDateString('pl-PL')}
              {s.valid_until && <span> → {new Date(s.valid_until).toLocaleDateString('pl-PL')}</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function OutgoingCompanyNode({ company, shares, onSelect, onDiscounts, onToggleActive, onDeleteShare, selectedShareId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-1">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-semibold text-purple-700 hover:bg-purple-50 rounded transition">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Building2 size={14} className="text-purple-500" />
        <span className="truncate">{company}</span>
        <span className="text-xs text-gray-400 ml-auto">{shares.length}</span>
      </button>
      {open && shares.map(s => (
        <div key={s.id} className={`flex items-center pl-8 pr-1 py-1 rounded hover:bg-purple-50 text-sm transition group ${!s.is_active ? 'opacity-50' : ''} ${selectedShareId === s.id ? 'bg-purple-100 ring-1 ring-purple-300' : ''}`}>
          <button onClick={() => onToggleActive(s)} className="p-1 flex-shrink-0 hover:bg-purple-100 rounded"
            title={s.is_active ? 'Dezaktywuj udostępnienie' : 'Aktywuj udostępnienie'}>
            {s.is_active ? <Eye size={14} className="text-green-500" /> : <EyeOff size={14} className="text-red-400" />}
          </button>
          <button onClick={() => onSelect(s)} className="flex-1 text-left min-w-0 ml-1">
            <div className="flex items-center gap-1">
              <span className="text-gray-700 truncate">{s.pricelist_name}</span>
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {new Date(s.created_at).toLocaleDateString('pl-PL')}
              {s.valid_until && <span> → {new Date(s.valid_until).toLocaleDateString('pl-PL')}</span>}
            </div>
          </button>
          <button onClick={() => onDiscounts(s)} className="p-1 opacity-0 group-hover:opacity-100 hover:bg-purple-100 rounded flex-shrink-0" title="Rabaty / Narzuty">
            <Tag size={14} className="text-purple-500" />
          </button>
          <button onClick={() => onDeleteShare(s)} className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded flex-shrink-0" title="Usuń udostępnienie">
            <Trash2 size={14} className="text-red-400" />
          </button>
        </div>
      ))}
    </div>
  );
}

function DiscountsManager({ shareId, toast }) {
  const { t } = useTranslation();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/pricelists/shares/${shareId}/discounts`); setDiscounts(r.data.discounts || []); }
    catch {} finally { setLoading(false); }
  }, [shareId]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editing) await api.put(`/pricelists/shares/${shareId}/discounts/${editing.id}`, data);
      else await api.post(`/pricelists/shares/${shareId}/discounts`, data);
      setShowForm(false); setEditing(null); load();
    } catch { toast.error(t('common.error')); }
  };
  const handleDelete = async (id) => {
    try { await api.delete(`/pricelists/shares/${shareId}/discounts/${id}`); load(); }
    catch { toast.error(t('common.error')); }
  };

  if (loading) return <div className="p-4 text-gray-400 text-sm">{t('common.loading')}</div>;

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">Rabaty i narzuty naliczane automatycznie przy kopiowaniu gałęzi (D&D). Warunki ilościowe sprawdzają wartość wybranej kolumny.</p>
      {discounts.length > 0 && (
        <div className="space-y-2">
          {discounts.map(d => (
            <div key={d.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${d.is_markup ? 'text-red-600' : 'text-green-600'}`}>
                    {d.is_markup ? 'Narzut' : 'Rabat'}: {d.discount_type === 'percent' ? `${d.value}%` : `${d.value} zł`}
                  </span>
                  {d.name && <span className="text-sm text-gray-600">— {d.name}</span>}
                  {!d.visible_to_recipient && <span className="text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">ukryty</span>}
                </div>
                {(d.condition_min != null || d.condition_max != null) && (
                  <div className="text-xs text-gray-400 mt-1">
                    Warunek (kol. {d.condition_column || 'B'}):
                    {d.condition_min != null && d.condition_max != null && ` ${d.condition_min}–${d.condition_max} ${d.condition_unit || ''}`}
                    {d.condition_min != null && d.condition_max == null && ` od ${d.condition_min} ${d.condition_unit || ''}`}
                    {d.condition_min == null && d.condition_max != null && ` do ${d.condition_max} ${d.condition_unit || ''}`}
                  </div>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => { setEditing(d); setShowForm(true); }} className="p-1 hover:bg-gray-200 rounded"><Pencil size={14} className="text-gray-500" /></button>
                <button onClick={() => handleDelete(d.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {discounts.length === 0 && !showForm && <div className="text-center py-4 text-gray-400 text-sm">Brak rabatów / narzutów</div>}
      {!showForm ? (
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-1 px-3 py-2 border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 rounded-lg text-sm w-full justify-center">
          <Plus size={14} /> Dodaj rabat / narzut
        </button>
      ) : (
        <DiscountForm key={editing?.id || 'new'} initial={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
      )}
    </div>
  );
}

function DiscountForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    discount_type: initial?.discount_type || 'percent',
    value: initial?.value || 0,
    is_markup: initial?.is_markup || false,
    condition_column: initial?.condition_column || '',
    condition_unit: initial?.condition_unit || '',
    condition_min: initial?.condition_min ?? '',
    condition_max: initial?.condition_max ?? '',
    visible_to_recipient: initial?.visible_to_recipient !== false,
  });
  const ic = "w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm";
  const lc = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lc}>Nazwa (opcjonalna)</label>
          <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={ic} placeholder="np. Rabat hurtowy" /></div>
        <div><label className={lc}>Typ</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({...form, is_markup: false})}
              className={`flex-1 py-1.5 rounded-lg text-sm font-medium ${!form.is_markup ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'}`}>Rabat</button>
            <button type="button" onClick={() => setForm({...form, is_markup: true})}
              className={`flex-1 py-1.5 rounded-lg text-sm font-medium ${form.is_markup ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500'}`}>Narzut</button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lc}>Wartość</label>
          <input type="number" value={form.value} onChange={e => setForm({...form, value: parseFloat(e.target.value) || 0})} className={ic} step="0.01" /></div>
        <div><label className={lc}>Rodzaj</label>
          <select value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value})} className={ic}>
            <option value="percent">Procentowy (%)</option>
            <option value="fixed">Kwotowy (zł)</option>
          </select></div>
      </div>

      <div className="border-t pt-3">
        <label className={lc}>Warunek ilościowy (opcjonalny)</label>
        <div className="grid grid-cols-4 gap-2">
          <div><label className="text-[10px] text-gray-400">Kolumna</label>
            <input type="text" value={form.condition_column} onChange={e => setForm({...form, condition_column: e.target.value.toUpperCase()})}
              className={ic} placeholder="B" maxLength={2} /></div>
          <div><label className="text-[10px] text-gray-400">Jednostka</label>
            <input type="text" value={form.condition_unit} onChange={e => setForm({...form, condition_unit: e.target.value})}
              className={ic} placeholder="m2" /></div>
          <div><label className="text-[10px] text-gray-400">Od (min)</label>
            <input type="number" value={form.condition_min} onChange={e => setForm({...form, condition_min: e.target.value})}
              className={ic} placeholder="—" step="0.01" /></div>
          <div><label className="text-[10px] text-gray-400">Do (max)</label>
            <input type="number" value={form.condition_max} onChange={e => setForm({...form, condition_max: e.target.value})}
              className={ic} placeholder="—" step="0.01" /></div>
        </div>
        <p className="text-xs text-gray-400 mt-1">Np. kolumna B (ilość), od 40 do 100 m2 — rabat naliczony gdy wartość kolumny mieści się w zakresie</p>
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={form.visible_to_recipient} onChange={e => setForm({...form, visible_to_recipient: e.target.checked})} className="rounded" />
          Widoczny dla odbiorcy
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button onClick={() => onSave({
          ...form,
          condition_min: form.condition_min !== '' ? parseFloat(form.condition_min) : null,
          condition_max: form.condition_max !== '' ? parseFloat(form.condition_max) : null,
          condition_column: form.condition_column || null,
          condition_unit: form.condition_unit || null,
        })} className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
          {initial ? 'Zapisz' : 'Dodaj'}
        </button>
      </div>
    </div>
  );
}

function PricelistPanel({ pricelists, panelId, canCreate, canEdit, canDelete, toast, dragState, setDragState, onCrossDrop, onSharePricelist, onRefreshPricelists }) {
  const { t } = useTranslation();
  const [selectedId, _setSelectedId] = useState(() => {
    const saved = localStorage.getItem(`swibi_pricelist_${panelId}`);
    return saved ? parseInt(saved) : null;
  });
  const setSelectedId = (id) => {
    _setSelectedId(id);
    if (id) localStorage.setItem(`swibi_pricelist_${panelId}`, id);
    else localStorage.removeItem(`swibi_pricelist_${panelId}`);
  };
  const [selectedSharedId, setSelectedSharedId] = useState(null);
  const [sharedPricelists, setSharedPricelists] = useState([]);
  const [outgoingShares, setOutgoingShares] = useState([]);
  const [discountShare, setDiscountShare] = useState(null);
  const [deleteShareTarget, setDeleteShareTarget] = useState(null);
  const [currentShareDiscounts, setCurrentShareDiscounts] = useState([]);
  const [currentShareMargins, setCurrentShareMargins] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [variables, setVariables] = useState({ global: [], local: [] });
  const [activeSpreadsheet, setActiveSpreadsheet] = useState(null);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [addParentId, setAddParentId] = useState(null);
  const [addItemType, setAddItemType] = useState('item');
  const [imageItem, setImageItem] = useState(null); // item being viewed for images
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showVarsPanel, setShowVarsPanel] = useState(false);
  const [plTab, setPlTab] = useState('own');
  const [plSearch, setPlSearch] = useState('');
  const [selectedOutgoingShareId, setSelectedOutgoingShareId] = useState(null);
  const [editStructure, setEditStructure] = useState(false);

  const selected = pricelists.find(p => p.id === selectedId);
  const isSelected = selectedId || selectedSharedId;
  const hasLocalVars = (variables.local || []).length > 0;
  const isConfigurator = hasLocalVars && selectedId && !selectedSharedId && !editStructure;
  const filteredOwn = pricelists.filter(p => !plSearch || p.name.toLowerCase().includes(plSearch.toLowerCase()));
  const filteredShared = sharedPricelists.filter(s => !plSearch || s.pricelist_name.toLowerCase().includes(plSearch.toLowerCase()) || s.owner_tenant_name.toLowerCase().includes(plSearch.toLowerCase()));
  const filteredOutgoing = outgoingShares.filter(s => !plSearch || s.pricelist_name.toLowerCase().includes(plSearch.toLowerCase()) || s.shared_with_tenant_name.toLowerCase().includes(plSearch.toLowerCase()));

  // Load shared pricelists (right panel)
  useEffect(() => {
    api.get('/pricelists/shares/my').then(r => setSharedPricelists(r.data.shares || [])).catch(() => {});
    api.get('/pricelists/shares/outgoing').then(r => setOutgoingShares(r.data.shares || [])).catch(() => {});
  }, []);

  // Reset selectedId if pricelist doesn't belong to current tenant
  useEffect(() => {
    if (selectedId && pricelists.length > 0 && !pricelists.find(p => p.id === selectedId)) {
      setSelectedId(null);
    }
  }, [selectedId, pricelists]);

  const load = useCallback(async () => {
    if (!selectedId && !selectedSharedId) return;
    setLoading(true);
    try {
      if (selectedSharedId) {
        // Load shared pricelist (cross-tenant, read-only)
        const res = await api.get(`/pricelists/shared/${selectedSharedId}`);
        setItems(res.data.items);
        setCurrentShareDiscounts(res.data.discounts || []);
        setCurrentShareMargins(res.data.margins || []);
        // Merge override variables into local vars
        const overrideVars = res.data.variables_override || {};
        const localVars = Object.entries(overrideVars).map(([key, value]) => ({ key, value, description: 'Override' }));
        setVariables({ global: [], local: localVars });
      } else {
        const [itemsRes, varsRes] = await Promise.all([
          api.get(`/pricelists/${selectedId}`),
          api.get(`/pricelists/${selectedId}/all-variables`)
        ]);
        setItems(itemsRes.data.items);
        setVariables(varsRes.data);
      }
    } catch { setSelectedId(null); setSelectedSharedId(null); }
    finally { setLoading(false); }
  }, [selectedId, selectedSharedId, toast]);

  useEffect(() => { load(); }, [load]);

  const handleSaveItem = async (data) => {
    try {
      if (editingItem) {
        await api.put(`/pricelists/items/${editingItem.id}`, data);
      } else {
        await api.post(`/pricelists/${selectedId}/items`, { ...data, parent_id: addParentId, item_type: addItemType });
      }
      setShowItemForm(false); setEditingItem(null); load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleDeleteItem = async () => {
    try {
      await api.delete(`/pricelists/items/${deleteTarget.id}`);
      setDeleteTarget(null); load();
    } catch { toast.error(t('common.error')); }
  };

  const handleDuplicate = async (node) => {
    if (!selectedId) return;
    try {
      await api.post(`/pricelists/items/${node.id}/copy`, {
        target_pricelist_id: selectedId,
        target_parent_id: node.parent_id || null
      });
      toast.success(t('pricelists.duplicated', { name: node.name }));
      load();
    } catch { toast.error(t('common.error')); }
  };

  const handleSpreadsheetSave = async (itemId, spreadsheet, resultValue) => {
    try {
      const res = await api.put(`/pricelists/items/${itemId}/spreadsheet`, { spreadsheet, result_value: resultValue });
      // Update local state with recalculated tree values
      if (res.data.tree_values) {
        const valMap = {};
        res.data.tree_values.forEach(v => { valMap[v.id] = parseFloat(v.result_value) || 0; });
        setItems(prev => prev.map(i => ({
          ...i,
          spreadsheet: i.id === itemId ? spreadsheet : i.spreadsheet,
          result_value: valMap[i.id] !== undefined ? valMap[i.id] : i.result_value,
        })));
      } else {
        setItems(prev => prev.map(i => i.id === itemId ? { ...i, spreadsheet, result_value: resultValue } : i));
      }
    } catch { toast.error(t('common.error')); }
  };

  const handleMove = async (draggedId, targetId, position) => {
    try {
      const target = items.find(i => i.id === targetId);
      if (!target) return;
      let newParentId, newPosition;
      if (position === 'inside') {
        newParentId = targetId;
        newPosition = items.filter(i => i.parent_id === targetId).length;
      } else {
        newParentId = target.parent_id || null;
        const siblings = items.filter(i => (i.parent_id || null) === (target.parent_id || null)).sort((a, b) => a.display_order - b.display_order);
        newPosition = siblings.findIndex(s => s.id === targetId) + (position === 'after' ? 1 : 0);
      }
      await api.put(`/pricelists/items/${draggedId}/move`, { parent_id: newParentId, position: newPosition });
      load();
    } catch { toast.error(t('common.error')); }
  };

  const handleLocalCrossDrop = async (sourceItemId, sourcePanelId, targetParentId, sourceSharedId) => {
    if (onCrossDrop) onCrossDrop(sourceItemId, selectedId, targetParentId, load, sourceSharedId);
  };

  const openAddForm = (parentId, type) => {
    setAddParentId(parentId); setAddItemType(type); setEditingItem(null); setShowItemForm(true);
  };

  const handleSaveLocalVar = async (data) => {
    try {
      await api.post(`/pricelists/${selectedId}/variables`, data);
      load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleUpdateVarValue = async (key, newValue) => {
    // Optimistic update for instant feedback
    setVariables(prev => ({
      ...prev,
      local: (prev.local || []).map(v => v.key === key ? { ...v, value: newValue } : v)
    }));
    try {
      await api.post(`/pricelists/${selectedId}/variables`, { key, value: newValue });
      load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const tree = buildTree(items);

  // Allow dropping on empty panel area
  const handlePanelDragOver = (e) => { e.preventDefault(); };
  const handlePanelDrop = (e) => {
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/x-pricelist-item'));
      if (data.panelId !== panelId && selectedId && onCrossDrop) {
        onCrossDrop(data.id, selectedId, null, load);
      }
    } catch { /* ignore */ }
  };

  const [showEditPricelist, setShowEditPricelist] = useState(false);
  const [confirmDeletePricelist, setConfirmDeletePricelist] = useState(false);
  const [showMargins, setShowMargins] = useState(false);

  const handleDeletePricelist = async () => {
    if (!selectedId) return;
    try {
      await api.delete(`/pricelists/${selectedId}`);
      toast.success(t('pricelists.pricelistDeleted'));
      setConfirmDeletePricelist(false);
      setSelectedId(null);
      setItems([]);
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleUpdatePricelist = async (data) => {
    try {
      await api.put(`/pricelists/${selectedId}`, data);
      toast.success(t('pricelists.pricelistUpdated'));
      setShowEditPricelist(false);
      // Refresh parent pricelist list
      if (onRefreshPricelists) onRefreshPricelists();
      load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden"
      onDragOver={handlePanelDragOver} onDrop={handlePanelDrop}>
      {/* Panel header */}
      <div className="bg-gray-50 border-b border-gray-200">
        {!isSelected ? (
          <>
            {/* Tabs (right panel) or simple header (left panel) */}
            {panelId === 'left' ? null : (
              <div className="flex items-center px-2 pt-2 gap-1">
                <button onClick={() => setPlTab('own')}
                  className={`flex-1 px-1.5 py-1.5 rounded-t text-sm font-medium transition ${plTab === 'own' ? 'bg-white text-emerald-700 border border-b-0 border-emerald-200' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t('pricelists.ownTab')} ({pricelists.length})
                </button>
                <button onClick={() => setPlTab('shared')}
                  className={`flex-1 px-1.5 py-1.5 rounded-t text-sm font-medium transition ${plTab === 'shared' ? 'bg-white text-blue-700 border border-b-0 border-blue-200' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t('pricelists.sharedToMe')} ({sharedPricelists.length})
                </button>
                <button onClick={() => setPlTab('outgoing')}
                  className={`flex-1 px-1.5 py-1.5 rounded-t text-sm font-medium transition ${plTab === 'outgoing' ? 'bg-white text-purple-700 border border-b-0 border-purple-200' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t('pricelists.sharedByMe')} ({outgoingShares.length})
                </button>
              </div>
            )}
            <div className="px-2 pb-2">
              <input type="text" value={plSearch} onChange={e => setPlSearch(e.target.value)}
                placeholder={t('pricelists.searchPricelist')} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mt-1 focus:ring-1 focus:ring-emerald-400" />
            </div>
          </>
        ) : (
          <>
            {/* Selected pricelist header */}
            <div className="px-3 py-2 flex items-center gap-2">
              <button onClick={() => { setSelectedId(null); setSelectedSharedId(null); setItems([]); setPlSearch(''); setEditStructure(false); }}
                className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-sm font-medium transition flex-shrink-0">
                ← Lista
              </button>
              <span className="text-sm font-medium text-gray-800 truncate flex-1">
                {selectedSharedId ? sharedPricelists.find(s => s.id === selectedSharedId)?.pricelist_name : selected?.name}
              </span>
              {selectedSharedId && (
                <>
                  <span className="text-sm bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex-shrink-0">
                    {sharedPricelists.find(s => s.id === selectedSharedId)?.owner_tenant_name}
                  </span>
                  <button onClick={() => setShowMargins(true)} className="p-1 text-orange-500 hover:bg-orange-50 rounded" title="Marże"><Variable size={16} /></button>
                </>
              )}
              {!selectedSharedId && (
                <>
                  {hasLocalVars && canEdit && (
                    <button onClick={() => setEditStructure(!editStructure)}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition ${editStructure ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                      {editStructure ? '✕ Zamknij edycję' : '⚙ Edytuj strukturę'}
                    </button>
                  )}
                  <button onClick={() => setShowEditPricelist(true)} className="p-1 text-gray-400 hover:bg-gray-100 rounded" title="Edytuj"><Pencil size={16} /></button>
                  {canDelete && <button onClick={() => setConfirmDeletePricelist(true)} className="p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 rounded" title="Usuń"><Trash2 size={16} /></button>}
                </>
              )}
              {!selectedSharedId && (
                <button onClick={() => setShowVarsPanel(!showVarsPanel)}
                  className={`p-1 rounded transition ${showVarsPanel ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:bg-gray-100'}`} title="Zmienne">
                  <Variable size={16} />
                </button>
              )}
              {canCreate && !selectedSharedId && (
                <>
                  <button onClick={() => openAddForm(null, 'group')} className="p-1 text-amber-600 hover:bg-amber-50 rounded" title="Grupa"><FolderOpen size={16} /></button>
                  <button onClick={() => openAddForm(null, 'item')} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" title="Pozycja"><Plus size={16} /></button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Configurator panel — editable variable inputs */}
      {isConfigurator && isSelected && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center gap-2 mb-2">
            <Variable size={14} className="text-amber-600" />
            <span className="text-xs font-semibold text-amber-700">Konfiguracja</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {(variables.local || []).map(v => (
              <div key={v.key} className="flex items-center gap-2">
                <label className="text-xs text-gray-600 w-36 truncate" title={`${v.key} — ${v.description || ''}`}>
                  {v.description || v.key}
                </label>
                <input type="number" value={v.value}
                  onChange={e => setVariables(prev => ({ ...prev, local: (prev.local || []).map(lv => lv.key === v.key ? { ...lv, value: e.target.value } : lv) }))}
                  onBlur={e => handleUpdateVarValue(v.key, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } }}
                  className="w-20 px-1.5 py-0.5 border border-amber-300 rounded text-xs text-right font-mono bg-white focus:ring-1 focus:ring-amber-400 focus:border-amber-400" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Variables panel (non-configurator) */}
      {showVarsPanel && selectedId && !isConfigurator && (
        <div className="px-3 py-2 bg-purple-50 border-b border-purple-200 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-purple-700">Zmienne cennika</span>
            <span className="text-purple-400">(l_)</span>
          </div>
          {(variables.global || []).map(v => (
            <span key={v.key} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-sm font-mono mr-1 mb-1">
              {v.key} = {v.value}
            </span>
          ))}
          {(variables.local || []).map(v => (
            <span key={v.key} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-sm font-mono mr-1 mb-1">
              {v.key} = {v.value}
              <span className="text-orange-400 font-sans">{v.description}</span>
            </span>
          ))}
          {canCreate && (
            <QuickAddVariable onAdd={handleSaveLocalVar} />
          )}
        </div>
      )}

      {/* Tree content */}
      <div className="flex-1 overflow-auto p-2">
        {!isSelected && (panelId === 'left' || plTab === 'own') && (
          <div className="space-y-0.5 p-1">
            {filteredOwn.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">{plSearch ? 'Brak wyników' : 'Brak cenników'}</div>
            ) : filteredOwn.map(pl => (
              <div key={pl.id} className="flex items-center rounded-lg hover:bg-emerald-50 transition group">
                <button onClick={() => { setSelectedId(pl.id); setSelectedSharedId(null); setSelectedOutgoingShareId(null); setActiveSpreadsheet(null); setPlSearch(''); }}
                  className={`flex-1 text-left px-3 py-2 flex items-center justify-between text-sm min-w-0 ${pl.is_active === false ? 'opacity-40' : ''}`}>
                  <span className="text-gray-700 truncate font-medium">{pl.name}{pl.is_active === false && <span className="ml-1.5 text-xs text-red-400">(nieaktywny)</span>}</span>
                  <span className="text-gray-400 flex-shrink-0 ml-2 text-xs">{pl.items_count || 0} poz.</span>
                </button>
                {canEdit && (<>
                  <button onClick={async () => {
                    try { await api.put(`/pricelists/${pl.id}`, { is_active: !pl.is_active }); if (onRefreshPricelists) onRefreshPricelists(); }
                    catch {}
                  }} className={`p-1.5 opacity-0 group-hover:opacity-100 rounded flex-shrink-0 ${pl.is_active !== false ? 'hover:bg-gray-100' : 'hover:bg-green-100'}`}
                    title={pl.is_active !== false ? 'Dezaktywuj' : 'Aktywuj'}>
                    {pl.is_active !== false ? <Eye size={14} className="text-gray-400" /> : <EyeOff size={14} className="text-red-400" />}
                  </button>
                  <button onClick={() => onSharePricelist && onSharePricelist(pl)}
                    className="p-1.5 mr-1 opacity-0 group-hover:opacity-100 hover:bg-emerald-100 rounded flex-shrink-0" title="Udostępnij">
                    <Share2 size={14} className="text-emerald-600" />
                  </button>
                </>)}
              </div>
            ))}
          </div>
        )}
        {!isSelected && panelId !== 'left' && plTab === 'shared' && (() => {
          if (filteredShared.length === 0) return <div className="text-center py-8 text-sm text-gray-400">{plSearch ? t('common.noResults') : t('pricelists.noSharedToMe')}</div>;
          // Group by company
          const byCompany = {};
          filteredShared.forEach(s => {
            const key = s.owner_tenant_name || 'System';
            if (!byCompany[key]) byCompany[key] = [];
            byCompany[key].push(s);
          });
          return (
            <div className="p-1">
              {Object.entries(byCompany).map(([company, shares]) => (
                <SharedCompanyNode key={company} company={company} shares={shares}
                  onSelect={(s) => { setSelectedSharedId(s.id); setSelectedId(null); setSelectedOutgoingShareId(null); setActiveSpreadsheet(null); setPlSearch(''); }} />
              ))}
            </div>
          );
        })()}
        {!isSelected && panelId !== 'left' && plTab === 'outgoing' && (() => {
          if (filteredOutgoing.length === 0) return <div className="text-center py-8 text-sm text-gray-400">{plSearch ? t('common.noResults') : t('pricelists.noSharedByMe')}</div>;
          const byCompany = {};
          filteredOutgoing.forEach(s => {
            const key = s.shared_with_tenant_name || 'System';
            if (!byCompany[key]) byCompany[key] = [];
            byCompany[key].push(s);
          });
          return (
            <div className="p-1">
              {Object.entries(byCompany).map(([company, shares]) => (
                <OutgoingCompanyNode key={company} company={company} shares={shares}
                  onSelect={(s) => { setSelectedId(s.pricelist_id); setSelectedSharedId(null); setSelectedOutgoingShareId(s.id); setActiveSpreadsheet(null); setPlSearch(''); }}
                  selectedShareId={selectedOutgoingShareId}
                  onDiscounts={(s) => setDiscountShare(s)}
                  onToggleActive={async (s) => {
                    try {
                      await api.put(`/pricelists/shares/${s.id}`, { is_active: !s.is_active });
                      setOutgoingShares(prev => prev.map(sh => sh.id === s.id ? { ...sh, is_active: !s.is_active } : sh));
                    } catch { toast.error(t('common.error')); }
                  }}
                  onDeleteShare={(s) => setDeleteShareTarget(s)} />
              ))}
            </div>
          );
        })()}
        {isSelected && loading ? (
          <div className="flex items-center justify-center h-32 text-sm text-gray-400">Ładowanie...</div>
        ) : isSelected && items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-sm text-gray-400">
            <Package size={32} className="mb-2 opacity-50" />
            <span>Cennik pusty</span>
            {canCreate && (
              <div className="flex gap-2 mt-2">
                <button onClick={() => openAddForm(null, 'group')} className="text-sm text-amber-600 hover:underline">+ Grupa</button>
                <button onClick={() => openAddForm(null, 'item')} className="text-sm text-emerald-600 hover:underline">+ Pozycja</button>
              </div>
            )}
          </div>
        ) : (
          tree.map(node => (
            <TreeNode key={node.id} node={node} depth={0}
              canEdit={selectedSharedId || isConfigurator ? false : canEdit} canDelete={selectedSharedId || isConfigurator ? false : canDelete} canCreate={selectedSharedId || isConfigurator ? false : canCreate}
              onEdit={(item) => { setEditingItem(item); setAddItemType(item.item_type); setShowItemForm(true); }}
              onDelete={setDeleteTarget} onAdd={openAddForm} onMove={handleMove}
              onSpreadsheetSave={handleSpreadsheetSave} activeSpreadsheet={activeSpreadsheet}
              setActiveSpreadsheet={setActiveSpreadsheet} dragState={dragState} setDragState={setDragState}
              variables={variables} panelId={panelId} onCrossDrop={handleLocalCrossDrop}
              onImages={setImageItem} sharedId={selectedSharedId || null}
              onDuplicate={selectedSharedId ? null : handleDuplicate}
              shareDiscounts={selectedSharedId ? currentShareDiscounts : null}
              shareMargins={selectedSharedId ? currentShareMargins : null} />
          ))
        )}
      </div>

      {/* Modals */}
      <Modal isOpen={showItemForm} onClose={() => { setShowItemForm(false); setEditingItem(null); }}
        title={editingItem ? t('common.edit') : (addItemType === 'group' ? t('pricelists.newGroup') : t('pricelists.newItem'))} maxWidth="max-w-lg">
        <ItemForm initial={editingItem} isGroup={editingItem ? editingItem.item_type === 'group' : addItemType === 'group'}
          onSave={handleSaveItem} onCancel={() => { setShowItemForm(false); setEditingItem(null); }} />
      </Modal>
      <ConfirmDialog isOpen={!!deleteTarget} type="danger"
        title="Usuń" message={`Usunąć "${deleteTarget?.name}"?${deleteTarget?.item_type === 'group' ? ' Zawartość grupy zostanie usunięta.' : ''}`}
        onConfirm={handleDeleteItem} onCancel={() => setDeleteTarget(null)} />

      {/* Delete pricelist confirm */}
      <ConfirmDialog isOpen={confirmDeletePricelist} type="danger"
        title="Usuń cennik" message={`Czy na pewno usunąć cennik "${selected?.name}"? Wszystkie pozycje i dane zostaną trwale usunięte.`}
        onConfirm={handleDeletePricelist} onCancel={() => setConfirmDeletePricelist(false)} />

      {/* Edit pricelist modal */}
      <Modal isOpen={showEditPricelist} onClose={() => setShowEditPricelist(false)} title="Edytuj cennik">
        <EditPricelistForm key={selectedId} pricelist={selected} onSave={handleUpdatePricelist} onCancel={() => setShowEditPricelist(false)} />
      </Modal>

      {/* Images modal */}
      {imageItem && (
        <Modal isOpen={!!imageItem} onClose={() => setImageItem(null)} title={`Zdjęcia: ${imageItem.name}`} maxWidth="max-w-2xl">
          <ItemImagesPanel itemId={imageItem.id} canEdit={canEdit} toast={toast} />
        </Modal>
      )}

      {/* Margins modal for shared pricelists */}
      {showMargins && selectedSharedId && (
        <MarginsModal shareId={selectedSharedId} items={items} toast={toast} onClose={() => setShowMargins(false)} />
      )}

      {/* Discounts modal for outgoing shares */}
      {discountShare && (
        <Modal isOpen={!!discountShare} onClose={() => setDiscountShare(null)}
          title={`Rabaty / Narzuty — ${discountShare.pricelist_name} → ${discountShare.shared_with_tenant_name}`} maxWidth="max-w-2xl">
          <DiscountsManager shareId={discountShare.id} toast={toast} />
        </Modal>
      )}

      {/* Delete share confirm */}
      <ConfirmDialog isOpen={!!deleteShareTarget} type="danger"
        title="Usuń udostępnienie"
        message={`Czy na pewno usunąć udostępnienie cennika "${deleteShareTarget?.pricelist_name}" dla firmy "${deleteShareTarget?.shared_with_tenant_name}"? Firma zostanie powiadomiona.`}
        onConfirm={async () => {
          try {
            await api.delete(`/pricelists/shares/${deleteShareTarget.id}`);
            setOutgoingShares(prev => prev.filter(s => s.id !== deleteShareTarget.id));
            setDeleteShareTarget(null);
            toast.success(t('common.deleted'));
          } catch { toast.error(t('common.error')); }
        }}
        onCancel={() => setDeleteShareTarget(null)} />
    </div>
  );
}

// ==================== MARGINS MODAL ====================

function MarginsModal({ shareId, items, toast, onClose }) {
  const [margins, setMargins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalMargin, setGlobalMargin] = useState({ type: 'percent', value: '' });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/pricelists/shares/${shareId}/margins`);
      setMargins(r.data.margins);
      const gm = r.data.margins.find(m => m.scope === 'global');
      if (gm) setGlobalMargin({ type: gm.margin_type, value: gm.margin_value });
    } catch {} finally { setLoading(false); }
  }, [shareId]);

  useEffect(() => { load(); }, [load]);

  const saveMargin = async (scope, targetItemId, type, value) => {
    try {
      await api.put(`/pricelists/shares/${shareId}/margins`, {
        scope, target_item_id: targetItemId, margin_type: type, margin_value: parseFloat(value) || 0
      });
      toast.success('Marża zapisana');
      load();
    } catch { toast.error('Błąd zapisu'); }
  };

  const deleteMargin = async (marginId) => {
    try { await api.delete(`/pricelists/shares/${shareId}/margins/${marginId}`); load(); }
    catch { toast.error('Błąd'); }
  };

  const groups = items.filter(i => i.item_type === 'group');
  const leafItems = items.filter(i => i.item_type === 'item');
  const ic = "px-2 py-1 border border-gray-300 rounded text-base";
  const getMarginFor = (scope, targetId) => margins.find(m => m.scope === scope && (m.target_item_id || null) === (targetId || null));

  return (
    <Modal isOpen={true} onClose={onClose} title="Marże na udostępnionym cenniku" maxWidth="max-w-2xl">
      {loading ? <div className="py-8 text-center text-gray-400">Ładowanie...</div> : (
        <div className="space-y-4">
          <p className="text-base text-gray-500">Marże naliczane automatycznie przy kopiowaniu (D&D) gałęzi do Twojego cennika lub oferty. Priorytet: pozycja &gt; grupa &gt; cały cennik.</p>

          {/* Global */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-base font-medium text-orange-800">Cały cennik</span>
              <div className="flex items-center gap-2">
                <select value={globalMargin.type} onChange={e => setGlobalMargin(g => ({...g, type: e.target.value}))} className={ic}>
                  <option value="percent">%</option><option value="fixed">zł</option>
                </select>
                <input type="number" step="any" value={globalMargin.value} onChange={e => setGlobalMargin(g => ({...g, value: e.target.value}))}
                  className={ic + " w-20 text-right"} placeholder="0" />
                <button onClick={() => saveMargin('global', null, globalMargin.type, globalMargin.value)}
                  className="px-2 py-1 bg-orange-600 text-white rounded text-base">Zapisz</button>
                {getMarginFor('global', null) && (
                  <button onClick={() => deleteMargin(getMarginFor('global', null).id)} className="text-base text-red-500 hover:underline">Usuń</button>
                )}
              </div>
            </div>
          </div>

          {/* Groups */}
          {groups.length > 0 && (
            <div>
              <h3 className="text-base font-semibold text-gray-500 uppercase mb-2">Per grupa</h3>
              {groups.map(g => {
                const m = getMarginFor('group', g.id);
                return (
                  <MarginRow key={g.id} label={g.name} margin={m} scope="group" targetId={g.id}
                    onSave={saveMargin} onDelete={m ? () => deleteMargin(m.id) : null} />
                );
              })}
            </div>
          )}

          {/* Items */}
          {leafItems.length > 0 && (
            <div>
              <h3 className="text-base font-semibold text-gray-500 uppercase mb-2">Per pozycja</h3>
              <div className="max-h-48 overflow-auto">
                {leafItems.map(item => {
                  const m = getMarginFor('item', item.id);
                  return (
                    <MarginRow key={item.id} label={item.name} margin={m} scope="item" targetId={item.id}
                      onSave={saveMargin} onDelete={m ? () => deleteMargin(m.id) : null} />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function MarginRow({ label, margin, scope, targetId, onSave, onDelete }) {
  const [type, setType] = useState(margin?.margin_type || 'percent');
  const [value, setValue] = useState(margin?.margin_value || '');
  const ic = "px-2 py-1 border border-gray-300 rounded text-base";

  return (
    <div className="flex items-center justify-between py-1.5 border-b last:border-0">
      <span className="text-base text-gray-700 truncate max-w-[220px]">{label}</span>
      <div className="flex items-center gap-1.5">
        <select value={type} onChange={e => setType(e.target.value)} className={ic}>
          <option value="percent">%</option><option value="fixed">zł</option>
        </select>
        <input type="number" step="any" value={value} onChange={e => setValue(e.target.value)}
          className={ic + " w-20 text-right"} placeholder="—" />
        <button onClick={() => onSave(scope, targetId, type, value)}
          className="px-2 py-1 bg-gray-600 text-white rounded text-base">OK</button>
        {onDelete && <button onClick={onDelete} className="text-base text-red-500">×</button>}
      </div>
    </div>
  );
}

// ==================== QUICK ADD VARIABLE (inline) ====================

function QuickAddVariable({ onAdd }) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const handleSubmit = () => {
    if (!key.trim()) return;
    onAdd({ key, value: parseFloat(value) || 0 });
    setKey(''); setValue('');
  };
  return (
    <div className="flex items-center gap-1 mt-1">
      <span className="text-orange-600 font-mono text-sm">l_</span>
      <input type="text" value={key} onChange={e => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
        className="w-20 px-1 py-0.5 border border-gray-300 rounded text-sm font-mono" placeholder="nazwa"
        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }} />
      <input type="number" step="any" value={value} onChange={e => setValue(e.target.value)}
        className="w-16 px-1 py-0.5 border border-gray-300 rounded text-sm" placeholder="wartość"
        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }} />
      <button onClick={handleSubmit} disabled={!key.trim()} className="px-1.5 py-0.5 bg-orange-600 disabled:opacity-40 text-white rounded text-sm">+</button>
    </div>
  );
}

// ==================== FORMULARZ POZYCJI / GRUPY ====================

function ItemForm({ initial, isGroup, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ name, description }); }} className="space-y-4">
      <div>
        <label className="block text-base font-medium text-gray-700 mb-1">Nazwa *</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          placeholder={isGroup ? 'np. Materiały budowlane' : 'np. Kalkulacja blach'} required autoFocus />
      </div>
      <div>
        <label className="block text-base font-medium text-gray-700 mb-1">Opis</label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" placeholder="Opcjonalny opis" />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-base text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" className="px-4 py-2 text-base bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">{initial ? 'Zapisz' : 'Dodaj'}</button>
      </div>
    </form>
  );
}

// ==================== FORMULARZ CENNIKA ====================

function PricelistForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: initial?.name || '', description: initial?.description || '', currency: initial?.currency || 'PLN',
    valid_from: initial?.valid_from?.split('T')[0] || '', valid_to: initial?.valid_to?.split('T')[0] || '', is_active: initial?.is_active ?? true,
  });
  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-4">
      <div>
        <label className="block text-base font-medium text-gray-700 mb-1">Nazwa *</label>
        <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-transparent" required autoFocus />
      </div>
      <div>
        <label className="block text-base font-medium text-gray-700 mb-1">Opis</label>
        <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" rows={2} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-base font-medium text-gray-700 mb-1">Waluta</label>
          <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base">
            <option value="PLN">PLN</option><option value="EUR">EUR</option><option value="USD">USD</option>
          </select>
        </div>
        <div>
          <label className="block text-base font-medium text-gray-700 mb-1">Ważny od</label>
          <input type="date" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
        </div>
        <div>
          <label className="block text-base font-medium text-gray-700 mb-1">Ważny do</label>
          <input type="date" value={form.valid_to} onChange={e => setForm({ ...form, valid_to: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
        </div>
      </div>
      {initial && <label className="flex items-center gap-2 text-base"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300" /> Cennik aktywny</label>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-base text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" className="px-4 py-2 text-base bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">{initial ? 'Zapisz' : 'Utwórz'}</button>
      </div>
    </form>
  );
}

// ==================== MODAL ZMIENNYCH GLOBALNYCH ====================

// ==================== SHARING MODAL ====================

function OutgoingShareRow({ share: s, onToggle, onDelete, toast }) {
  const { t } = useTranslation();
  const [showDiscounts, setShowDiscounts] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg mb-2">
      <div className="flex items-center justify-between p-3 group hover:bg-gray-50">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-800">{s.pricelist_name}</div>
          <div className="text-xs text-gray-500 flex items-center gap-3">
            <span><Building2 size={12} className="inline mr-1" />{s.shared_with_tenant_name}</span>
            <span>{new Date(s.created_at).toLocaleDateString('pl-PL')}</span>
            {s.valid_until && <span>→ {new Date(s.valid_until).toLocaleDateString('pl-PL')}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setShowDiscounts(!showDiscounts)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition ${showDiscounts ? 'bg-amber-100 text-amber-700' : 'text-gray-500 hover:bg-gray-100'}`}>
            <Tag size={12} /> Rabaty
          </button>
          <button onClick={() => onToggle(s)}
            className={`px-2 py-1 rounded text-xs ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {s.is_active ? 'Aktywne' : 'Wstrzymane'}
          </button>
          <button onClick={() => onDelete(s.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
        </div>
      </div>
      {showDiscounts && (
        <div className="border-t border-gray-100 p-3 bg-amber-50/50">
          <DiscountsManager shareId={s.id} toast={toast} />
        </div>
      )}
    </div>
  );
}

function SharingModal({ isOpen, onClose, pricelists, toast }) {
  const [tab, setTab] = useState('outgoing');
  const [outgoing, setOutgoing] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newShare, setNewShare] = useState({ pricelist_id: '', shared_with_tenant_id: '', valid_until: '', notes: '', variables_override: {} });
  const [pricelistVars, setPricelistVars] = useState([]); // local vars of selected pricelist

  const load = useCallback(async () => {
    try {
      const [o, i, t] = await Promise.all([
        api.get('/pricelists/shares/outgoing'),
        api.get('/pricelists/shares/my'),
        api.get('/pricelists/shares/tenants'),
      ]);
      setOutgoing(o.data.shares); setIncoming(i.data.shares); setTenants(t.data.tenants);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isOpen) { setLoading(true); load(); } }, [isOpen, load]);

  // Load local variables when pricelist changes
  useEffect(() => {
    if (!newShare.pricelist_id) { setPricelistVars([]); return; }
    api.get(`/pricelists/${newShare.pricelist_id}/variables`)
      .then(r => {
        setPricelistVars(r.data.variables || []);
        // Pre-fill override with current values
        const override = {};
        (r.data.variables || []).forEach(v => { override[v.key] = v.value; });
        setNewShare(prev => ({ ...prev, variables_override: override }));
      })
      .catch(() => setPricelistVars([]));
  }, [newShare.pricelist_id]);

  const handleCreate = async () => {
    try {
      await api.post('/pricelists/shares', {
        pricelist_id: parseInt(newShare.pricelist_id),
        shared_with_tenant_id: parseInt(newShare.shared_with_tenant_id),
        valid_until: newShare.valid_until || null,
        notes: newShare.notes || null,
        variables_override: newShare.variables_override || {},
      });
      toast.success('Cennik udostępniony');
      setShowNew(false); setNewShare({ pricelist_id: '', shared_with_tenant_id: '', valid_until: '', notes: '', variables_override: '{}' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Błąd'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/pricelists/shares/${id}`); load(); } catch { toast.error('Błąd'); }
  };

  const handleToggle = async (share) => {
    try { await api.put(`/pricelists/shares/${share.id}`, { is_active: !share.is_active }); load(); } catch {}
  };

  const ic = "w-full px-3 py-1.5 border border-gray-300 rounded-lg text-base";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Udostępnianie cenników" maxWidth="max-w-3xl">
      <div className="flex gap-1 border-b mb-4">
        {[{ k: 'outgoing', l: 'Udostępnione przeze mnie' }, { k: 'incoming', l: 'Udostępnione mi' }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-base font-medium border-b-2 transition ${tab === t.k ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500'}`}>
            {t.l}
            {t.k === 'outgoing' && outgoing.length > 0 && <span className="ml-1 text-base text-gray-400">({outgoing.length})</span>}
            {t.k === 'incoming' && incoming.length > 0 && <span className="ml-1 text-base text-gray-400">({incoming.length})</span>}
          </button>
        ))}
      </div>

      {loading ? <div className="py-8 text-center text-gray-400 text-base">Ładowanie...</div> : (
        <>
          {tab === 'outgoing' && (
            <div>
              <div className="flex justify-end mb-3">
                <button onClick={() => setShowNew(!showNew)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-base">
                  <Plus size={16} /> Udostępnij cennik
                </button>
              </div>
              {showNew && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-base text-gray-600 mb-1">Cennik *</label>
                      <select value={newShare.pricelist_id} onChange={e => setNewShare({...newShare, pricelist_id: e.target.value})} className={ic}>
                        <option value="">Wybierz...</option>
                        {pricelists.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select></div>
                    <div><label className="block text-base text-gray-600 mb-1">Firma docelowa *</label>
                      <select value={newShare.shared_with_tenant_id} onChange={e => setNewShare({...newShare, shared_with_tenant_id: e.target.value})} className={ic}>
                        <option value="">Wybierz...</option>
                        {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-base text-gray-600 mb-1">Ważne do</label>
                      <input type="date" value={newShare.valid_until} onChange={e => setNewShare({...newShare, valid_until: e.target.value})} className={ic} /></div>
                    <div><label className="block text-base text-gray-600 mb-1">Notatki</label>
                      <input type="text" value={newShare.notes} onChange={e => setNewShare({...newShare, notes: e.target.value})} className={ic} /></div>
                  </div>
                  {/* Zmienne lokalne cennika */}
                  {pricelistVars.length > 0 && (
                    <div>
                      <label className="block text-base text-gray-600 mb-2">Wartości zmiennych dla tej firmy</label>
                      <div className="bg-white border border-gray-200 rounded-lg p-2 space-y-2">
                        {pricelistVars.map(v => (
                          <div key={v.key} className="flex items-center gap-2">
                            <span className="text-base font-mono text-orange-600 w-36 flex-shrink-0" title={v.description || ''}>
                              {v.key}
                              {v.description && <span className="font-sans text-gray-400 ml-1">({v.description})</span>}
                            </span>
                            <span className="text-sm text-gray-400 flex-shrink-0">domyślnie: {v.value}</span>
                            <input type="number" step="any"
                              value={newShare.variables_override[v.key] ?? v.value}
                              onChange={e => setNewShare(prev => ({
                                ...prev,
                                variables_override: { ...prev.variables_override, [v.key]: parseFloat(e.target.value) || 0 }
                              }))}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-base text-right" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {pricelistVars.length === 0 && newShare.pricelist_id && (
                    <div className="text-base text-gray-400 italic">Ten cennik nie ma zdefiniowanych zmiennych lokalnych. Dodaj je w panelu zmiennych cennika.</div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowNew(false)} className="px-3 py-1.5 text-base text-gray-500">Anuluj</button>
                    <button onClick={handleCreate} disabled={!newShare.pricelist_id || !newShare.shared_with_tenant_id}
                      className="px-3 py-1.5 bg-purple-600 disabled:opacity-40 text-white rounded text-base">Udostępnij</button>
                  </div>
                </div>
              )}
              {outgoing.length === 0 && !showNew && <div className="text-center py-8 text-gray-400 text-base">Nie udostępniasz żadnych cenników</div>}
              {outgoing.map(s => (
                <OutgoingShareRow key={s.id} share={s} onToggle={handleToggle} onDelete={handleDelete} toast={toast} />
              ))}
            </div>
          )}

          {tab === 'incoming' && (
            <div>
              {incoming.length === 0 && <div className="text-center py-8 text-gray-400 text-base">Nikt nie udostępnił Ci cenników</div>}
              {incoming.map(s => (
                <div key={s.id} className="p-3 border border-gray-200 rounded-lg mb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-base font-medium text-gray-800">{s.pricelist_name}</div>
                      <div className="text-base text-gray-500 flex items-center gap-3">
                        <span><Building2 size={12} className="inline mr-1" />{s.owner_tenant_name}</span>
                        <span>Od: {s.shared_by_name}</span>
                        {s.valid_until && <span>Do: {new Date(s.valid_until).toLocaleDateString('pl-PL')}</span>}
                      </div>
                    </div>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-base">{s.access_level}</span>
                  </div>
                  {s.variables_override && Object.keys(s.variables_override).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(s.variables_override).map(([k, v]) => (
                        <span key={k} className="text-sm font-mono bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded">{k}={v}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ==================== GLOBAL VARIABLES MODAL ====================

function GlobalVariablesModal({ isOpen, onClose, toast, canEdit }) {
  const [vars, setVars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newVar, setNewVar] = useState({ key: '', value: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ value: '', description: '' });

  const load = useCallback(async () => {
    try { const res = await api.get('/pricelists/variables/global'); setVars(res.data.variables); }
    catch { /* */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isOpen) { setLoading(true); load(); } }, [isOpen, load]);

  const handleAdd = async () => {
    if (!newVar.key.trim()) return;
    try {
      await api.post('/pricelists/variables/global', { key: newVar.key, value: parseFloat(newVar.value) || 0, description: newVar.description });
      toast.success('Dodano'); setNewVar({ key: '', value: '', description: '' }); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Błąd'); }
  };
  const handleUpdate = async (v) => {
    try {
      await api.put(`/pricelists/variables/global/${v.id}`, { value: parseFloat(editForm.value) || 0, description: editForm.description });
      setEditingId(null); load();
    } catch { toast.error('Błąd'); }
  };
  const handleDelete = async (v) => {
    try { await api.delete(`/pricelists/variables/global/${v.id}`); load(); } catch { toast.error('Błąd'); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Zmienne globalne (g_)" maxWidth="max-w-2xl">
      <p className="text-base text-gray-500 mb-4">Dostępne we wszystkich cennikach firmy.</p>
      {loading ? <div className="py-8 text-center text-gray-400 text-base">Ładowanie...</div> : (
        <table className="w-full text-base mb-4">
          <thead><tr className="text-base text-gray-500 border-b">
            <th className="text-left px-3 py-2 font-medium">Zmienna</th>
            <th className="text-left px-3 py-2 font-medium w-28">Wartość</th>
            <th className="text-left px-3 py-2 font-medium">Opis</th>
            {canEdit && <th className="w-24"></th>}
          </tr></thead>
          <tbody>
            {vars.map(v => (
              <tr key={v.id} className="border-b border-gray-100 hover:bg-gray-50 group/gv">
                <td className="px-3 py-2 text-base font-mono font-medium text-blue-600">{v.key}</td>
                <td className="px-3 py-2 text-base">
                  {editingId === v.id ? <input type="number" step="any" value={editForm.value} onChange={e => setEditForm({ ...editForm, value: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') handleUpdate(v); }} className="w-24 px-2 py-1 border border-gray-300 rounded text-base" autoFocus />
                    : <span>{v.value}</span>}
                </td>
                <td className="px-3 py-2 text-base text-gray-500">
                  {editingId === v.id ? <input type="text" value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') handleUpdate(v); }} className="w-full px-2 py-1 border border-gray-300 rounded text-base" />
                    : (v.description || '—')}
                </td>
                {canEdit && <td className="px-2 py-2 text-right">
                  {editingId === v.id ? (
                    <><button onClick={() => handleUpdate(v)} className="text-base text-emerald-600 hover:underline mr-2">OK</button>
                    <button onClick={() => setEditingId(null)} className="text-base text-gray-400 hover:underline">Anuluj</button></>
                  ) : (
                    <div className="flex gap-1 justify-end opacity-0 group-hover/gv:opacity-100">
                      <button onClick={() => { setEditingId(v.id); setEditForm({ value: v.value, description: v.description || '' }); }} className="p-1 hover:bg-gray-100 rounded"><Pencil size={14} className="text-gray-500" /></button>
                      <button onClick={() => handleDelete(v)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
                    </div>
                  )}
                </td>}
              </tr>
            ))}
            {vars.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-base text-gray-400">Brak zmiennych</td></tr>}
          </tbody>
        </table>
      )}
      {canEdit && (
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <span className="text-base font-mono text-blue-600">g_</span>
          <input type="text" value={newVar.key} onChange={e => setNewVar({ ...newVar, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
            className="w-36 px-2 py-1.5 border border-gray-300 rounded text-base font-mono" placeholder="nazwa" />
          <input type="number" step="any" value={newVar.value} onChange={e => setNewVar({ ...newVar, value: e.target.value })}
            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-base" placeholder="wartość" />
          <input type="text" value={newVar.description} onChange={e => setNewVar({ ...newVar, description: e.target.value })}
            className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-base" placeholder="opis" />
          <button onClick={handleAdd} disabled={!newVar.key.trim()} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded text-base font-medium">Dodaj</button>
        </div>
      )}
    </Modal>
  );
}

// ==================== SPLIT PANELS (resizable) ====================

function SplitPanels({ left, right }) {
  const [splitPercent, setSplitPercent] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const handleMouseDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (e) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.min(80, Math.max(20, (x / rect.width) * 100));
      setSplitPercent(pct);
    };

    const handleMouseUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div ref={containerRef} className="flex-1 flex min-h-0">
      <div style={{ width: `${splitPercent}%` }} className="min-w-0 flex">
        {left}
      </div>
      <div
        onMouseDown={handleMouseDown}
        className="w-2 flex-shrink-0 cursor-col-resize group flex items-center justify-center hover:bg-emerald-100 transition-colors relative"
        title="Przeciągnij aby zmienić szerokość"
      >
        <div className="w-0.5 h-8 bg-gray-300 group-hover:bg-emerald-500 rounded-full transition-colors" />
      </div>
      <div style={{ width: `${100 - splitPercent}%` }} className="min-w-0 flex">
        {right}
      </div>
    </div>
  );
}

// ==================== GŁÓWNY KOMPONENT ====================

export default function PricelistsPage() {
  const { t } = useTranslation();
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const toast = useToast();
  const [pricelists, setPricelists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPricelist, setEditingPricelist] = useState(null);
  const [showGlobalVars, setShowGlobalVars] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showSharing, setShowSharing] = useState(false);
  const [sharePricelist, setSharePricelist] = useState(null);
  const [dragState, setDragState] = useState({ draggingId: null, overId: null, overPosition: null, sourcePanel: null });

  const loadPricelists = useCallback(async () => {
    try { const res = await api.get('/pricelists'); setPricelists(res.data.items); }
    catch { toast.error(t('pricelists.loadingError')); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadPricelists(); }, [loadPricelists]);

  const handleSavePricelist = async (data) => {
    try {
      if (editingPricelist) { await api.put(`/pricelists/${editingPricelist.id}`, data); }
      else { await api.post('/pricelists', data); }
      setShowForm(false); setEditingPricelist(null); loadPricelists();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  // Cross-panel move/copy
  const handleCrossDrop = async (sourceItemId, targetPricelistId, targetParentId, reloadFn, sourceSharedId) => {
    try {
      if (sourceSharedId) {
        // Copy from shared pricelist (cross-tenant, always copy)
        await api.post('/pricelists/shared/copy-item', {
          share_id: sourceSharedId,
          source_item_id: sourceItemId,
          target_pricelist_id: targetPricelistId,
          target_parent_id: targetParentId
        });
        toast.success(t('pricelists.copiedFromShared'));
      } else {
        // Move between own pricelists (copy + delete source)
        await api.post(`/pricelists/items/${sourceItemId}/copy`, {
          target_pricelist_id: targetPricelistId,
          target_parent_id: targetParentId,
          move: true
        });
        toast.success(t('pricelists.branchMoved'));
      }
      // Reload both panels
      if (reloadFn) reloadFn();
      // Force reload of the other panel too
      loadPricelists();
    } catch (err) {
      toast.error(err.response?.data?.error || t('common.error'));
    }
  };

  if (!canView('pricelists')) {
    return <div className="p-8 text-center text-gray-400"><BookOpen size={48} className="mx-auto mb-4 opacity-50" /><p>{t('common.noAccess')}</p></div>;
  }

  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  return (
    <div className="p-4 h-full flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <BookOpen size={24} className="text-emerald-600" />
          <h1 className="text-xl font-bold text-gray-800">{t('pricelists.title')}</h1>
          <span className="text-base text-gray-400">{pricelists.length} cenników</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowGlobalVars(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-lg text-base transition">
            <Globe size={16} /> {t('pricelists.globalVariables')}
          </button>
          <button onClick={() => setShowSharing(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-lg text-base transition">
            <Share2 size={16} /> {t('pricelists.sharing')}
          </button>
          {canCreate('pricelists') && (
            <>
              <button onClick={() => setShowImport(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-lg text-base transition">
                <Upload size={16} /> {t('pricelists.importButton')}
              </button>
              <button onClick={() => { setEditingPricelist(null); setShowForm(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-base transition">
                <Plus size={16} /> {t('pricelists.newPricelist')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Hint */}
      <div className="text-base text-gray-400 mb-2 flex items-center gap-1">
        <Copy size={14} /> Przeciągnij gałąź z jednego panelu do drugiego aby skopiować
      </div>

      {/* Dual panels with resizable splitter */}
      <SplitPanels
        left={<PricelistPanel pricelists={pricelists} panelId="left"
          canCreate={canCreate('pricelists')} canEdit={canEdit('pricelists')} canDelete={canDelete('pricelists')}
          toast={toast} dragState={dragState} setDragState={setDragState} onCrossDrop={handleCrossDrop}
          onSharePricelist={(pl) => { setSharePricelist(pl); setShowSharing(true); }} onRefreshPricelists={loadPricelists} />}
        right={<PricelistPanel pricelists={pricelists} panelId="right"
          canCreate={canCreate('pricelists')} canEdit={canEdit('pricelists')} canDelete={canDelete('pricelists')}
          toast={toast} dragState={dragState} setDragState={setDragState} onCrossDrop={handleCrossDrop}
          onSharePricelist={(pl) => { setSharePricelist(pl); setShowSharing(true); }} onRefreshPricelists={loadPricelists} />}
      />

      {/* Modals */}
      <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditingPricelist(null); }}
        title={editingPricelist ? t('pricelists.editPricelist') : t('pricelists.newPricelist')}>
        <PricelistForm key={editingPricelist?.id || 'new'} initial={editingPricelist} onSave={handleSavePricelist} onCancel={() => { setShowForm(false); setEditingPricelist(null); }} />
      </Modal>
      <GlobalVariablesModal isOpen={showGlobalVars} onClose={() => setShowGlobalVars(false)} toast={toast} canEdit={canCreate('pricelists')} />
      <ImportDialog isOpen={showImport} onClose={() => setShowImport(false)} toast={toast}
        onCreated={() => loadPricelists()} />
      <SharingModal isOpen={showSharing} onClose={() => setShowSharing(false)} pricelists={pricelists} toast={toast} />
    </div>
  );
}
